import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  RolesService,
  VehiclesService,
} from '@boma/supplier-portal/shared/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { fetch } from '@nx/angular';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import * as VehiclesActions from './vehicles.actions';

@Injectable()
export class VehiclesEffects {
  loadVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.loadVehicles),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { vehicles } = state;
          return this.vehicleService
            .loadVehicles(
              user.selectedOrganizationId,
              action.page || vehicles.page,
              vehicles.perPage,
              action.query || vehicles.query
            )
            .pipe(
              map((response) =>
                VehiclesActions.loadVehiclesSuccess({
                  payload: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.loadVehiclesFailure({ error });
        },
      })
    )
  );

  searchVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.searchVehicles),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { vehicles } = state;
          console.log('search vehicles');
          return this.vehicleService
            .loadVehicles(
              user.selectedOrganizationId,
              vehicles.page,
              vehicles.perPage,
              action?.query
            )
            .pipe(
              map((response) =>
                VehiclesActions.searchVehiclesSuccess({
                  payload: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.searchVehiclesFailure({ error });
        },
      })
    )
  );

  loadVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.loadVehicle),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.vehicleService
            .loadVehicle(user.selectedOrganizationId, action.id)
            .pipe(
              map((response) =>
                VehiclesActions.loadVehicleSuccess({
                  vehicle: response,
                })
              )
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.loadVehicleFailure({ error });
        },
      })
    )
  );

  createVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.createVehicle),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.vehicleService
            .createVehicle(user.selectedOrganizationId, action.vehicle)
            .pipe(
              tap((response) =>
                this.router.navigate(['/', 'vehicles', response.id])
              ),
              map((response) =>
                VehiclesActions.createVehicleSuccess({
                  vehicle: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.createVehicleFailure({ error });
        },
      })
    )
  );

  updateVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.updateVehicle),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { vehicles } = state;
          return this.vehicleService
            .updateVehicle(
              user.selectedOrganizationId,
              vehicles.selectedId,
              action.vehicle
            )
            .pipe(
              map((response) =>
                VehiclesActions.updateVehicleSuccess({
                  vehicle: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.updateVehicleFailure({ error });
        },
      })
    )
  );

  updateVehiclePermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.updateVehiclePermissions),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.vehicleService
            .updateVehiclePermissions(
              user.selectedOrganizationId,
              action.vehicleId,
              action.userId,
              action.roleIds
            )
            .pipe(
              map((response) =>
                VehiclesActions.updateVehiclePermissionsSuccess({
                  payload: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.updateVehiclePermissionsFailure({ error });
        },
      })
    )
  );

  loadRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.loadRoles),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.roleSvc
            .listRoles(user.selectedOrganizationId, '1', '25', '', 'Vehicle')
            .pipe(
              map((response) =>
                VehiclesActions.loadRolesSuccess({ payload: response })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.loadRolesFailure({ error });
        },
      })
    )
  );

  loadAdministrators$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.loadAdministrators),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.vehicleService
            .listDrivers(
              user.selectedOrganizationId,
              action.vehicleId,
              action.query
            )
            .pipe(
              map((response) =>
                VehiclesActions.loadAdministratorsSuccess({
                  administrators: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.loadAdministratorsFailure({ error });
        },
      })
    )
  );

  loadAdministrator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.loadAdministratorDetails),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.vehicleService
            .loadDriverDetails(
              user.selectedOrganizationId,
              action.vehicleId,
              action.adminId
            )
            .pipe(
              map((response) =>
                VehiclesActions.loadAdministratorDetailsSuccess({
                  administrator: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.loadAdministratorDetailsFailure({
            error,
          });
        },
      })
    )
  );

  deleteAdministrator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.deleteAdministrator),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.vehicleService
            .deleteDriver(
              user.selectedOrganizationId,
              action.vehicleId,
              action.userId
            )
            .pipe(
              map((response) =>
                VehiclesActions.deleteAdministratorSuccess({
                  userId: action.userId,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.loadAdministratorDetailsFailure({
            error,
          });
        },
      })
    )
  );

  createAdministrator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.createAdministrator),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.vehicleService
            .createDriver(
              user.selectedOrganizationId,
              action.vehicleId,
              action.emailAddressOrPhoneNumber,
              action.roleIds
            )
            .pipe(
              map((response) =>
                VehiclesActions.createAdministratorSuccess({
                  administrator: response,
                })
              )
            );
        },
        onError: (action, error) => {
          return VehiclesActions.loadAdministratorDetailsFailure({
            error,
          });
        },
      })
    )
  );

  updateAdministrator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActions.updateAdministrator),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.vehicleService
            .updateDriver(
              user.selectedOrganizationId,
              action.vehicleId,
              action.userId,
              action.roleIds
            )
            .pipe(
              map((response) =>
                VehiclesActions.updateAdministratorSuccess({
                  administrator: response,
                })
              )
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return VehiclesActions.loadAdministratorDetailsFailure({
            error,
          });
        },
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private vehicleService: VehiclesService,
    private roleSvc: RolesService,
    private router: Router
  ) {}
}
